import { useMemo, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { CalendarClock, Clock, Copy, ExternalLink, Fingerprint, KeyRound, Plus, Repeat, RotateCcw, Search, ShieldAlert, ShieldX, Star, Trash2, UserRound, X } from "lucide-react";
import { useDocumentTitle } from "../../app/useDocumentTitle.js";
import { useMediaQuery } from "../../app/useMediaQuery.js";
import { paths } from "../../app/paths.js";
import { useBreachScan } from "../../data/breach.js";
import { useRestoreItem, useToggleFavorite, useTrashItems, useVaultEntries } from "../../data/vault.js";
import type { VaultMeta } from "../../api/vault.js";
import { expiringIds, filterEntries, healthInputs, sortEntries } from "../../lib/entries.js";
import type { Entry, SortOrder } from "../../lib/entries.js";
import { analyzeHealth } from "../../lib/health.js";
import { formatRelativeTime } from "../../lib/format.js";
import { hasPasskey } from "../../lib/passkeys.js";
import { displayHost, safeHttpUrl } from "../../lib/url.js";
import { Dialog } from "../../ui/Dialog.js";
import { SelectInput, TextInput } from "../../ui/forms.js";
import { Alert, Avatar, Badge, Button, EmptyState, IconButton, Kbd, PageHeader, Skeleton, buttonClass } from "../../ui/primitives.js";
import { useToast } from "../../ui/Toast.js";
import { fieldLabels, itemTypeLabel } from "../../vaultPayload.js";
import type { ItemType } from "../../vaultPayload.js";
import { ItemInspector } from "./ItemInspector.js";
import { TYPE_INFOS, typeFromParam, typeInfo } from "./itemTypes.js";
import type { ItemTypeInfo } from "./itemTypes.js";
import { useEntryActions } from "./useEntryActions.js";
import type { InspectorMode } from "./ItemInspector.js";

const VIEW_TITLES: Record<string, string> = {
  all: "Alle Einträge",
  favorites: "Favoriten",
  trash: "Papierkorb"
};

type Flag = "breached" | "weak" | "reused" | "old" | "expiring";

const FLAG_LABEL: Record<Flag, string> = {
  breached: "In Datenlecks gefunden",
  weak: "Schwaches Passwort",
  reused: "Mehrfach verwendet",
  old: "Über ein Jahr unverändert",
  expiring: "Läuft bald ab oder ist abgelaufen"
};

const FLAG_ICON = { breached: ShieldX, weak: ShieldAlert, reused: Repeat, old: Clock, expiring: CalendarClock } as const;

const FILTER_TITLES: Record<string, string> = {
  breached: "In Datenlecks gefunden",
  weak: "Schwache Passwörter",
  reused: "Mehrfach verwendete Passwörter",
  old: "Lange unverändert",
  expiring: "Bald ablaufend"
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function VaultPage(): ReactNode {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const entries = useVaultEntries();
  const trash = useTrashItems();
  const toggleFavorite = useToggleFavorite();
  const restore = useRestoreItem();
  const actions = useEntryActions();
  const wide = useMediaQuery("(min-width: 1280px)");
  const [order, setOrder] = useState<SortOrder>("updated");
  const [query, setQuery] = useState("");

  const view = searchParams.get("view") ?? "all";
  const folder = searchParams.get("folder") ?? undefined;
  const filter = searchParams.get("filter") ?? undefined;
  const type = typeFromParam(searchParams.get("type"));
  const isTrash = view === "trash";
  const itemId = params["id"];
  const isNew = location.pathname === "/vault/new";
  const isEdit = location.pathname.endsWith("/edit") && itemId !== undefined;
  const listSearch = searchParams.toString() === "" ? "" : `?${searchParams.toString()}`;

  const navState: unknown = location.state;
  const prefill = isRecord(navState) && typeof navState["password"] === "string" ? navState["password"] : undefined;

  const all: ReadonlyArray<Entry> = useMemo((): ReadonlyArray<Entry> => entries.data?.entries ?? [], [entries.data]);
  const now = useMemo((): number => Date.now(), [entries.dataUpdatedAt]);
  const breach = useBreachScan(all);
  const health = useMemo(
    () => analyzeHealth(healthInputs(all), now, breach.breached),
    [all, now, breach.breached]
  );
  const expiring = useMemo((): string[] => expiringIds(all, now), [all, now]);
  const healthIds = filter === "breached"
    ? health.breachedIds
    : filter === "weak"
    ? health.weakIds
    : filter === "reused"
      ? health.reusedIds
      : filter === "old"
        ? health.oldIds
        : filter === "expiring"
          ? expiring
          : undefined;
  const visible = sortEntries(
    filterEntries(all, {
      query,
      favoritesOnly: view === "favorites",
      folder,
      type,
      ids: healthIds !== undefined ? new Set(healthIds) : undefined
    }),
    order
  );

  const selected = itemId !== undefined && !isNew ? all.find((e: Entry): boolean => e.meta.id === itemId) : undefined;
  const missing = itemId !== undefined && !isNew && !entries.isPending && selected === undefined;
  const inspectorOpen = isNew || itemId !== undefined;
  const inspectorMode: InspectorMode = isNew ? "new" : isEdit ? "edit" : "view";
  const folders = [...new Set(all.map((e: Entry): string => e.meta.folder))].sort((a: string, b: string): number => a.localeCompare(b, "de"));

  const title = filter !== undefined && FILTER_TITLES[filter] !== undefined
    ? FILTER_TITLES[filter]
    : folder ?? (type !== undefined ? typeInfo(type).plural : undefined) ?? VIEW_TITLES[view] ?? VIEW_TITLES.all ?? "Tresor";
  useDocumentTitle(title);

  const flags = useMemo((): ReadonlyMap<string, ReadonlyArray<Flag>> => {
    const map = new Map<string, Flag[]>();
    const add = (id: string, flag: Flag): void => {
      map.set(id, [...(map.get(id) ?? []), flag]);
    };
    health.breachedIds.forEach((id: string): void => add(id, "breached"));
    health.weakIds.forEach((id: string): void => add(id, "weak"));
    health.reusedIds.forEach((id: string): void => add(id, "reused"));
    health.oldIds.forEach((id: string): void => add(id, "old"));
    expiring.forEach((id: string): void => add(id, "expiring"));
    return map;
  }, [health, expiring]);

  function setType(next: ItemType | undefined): void {
    const params = new URLSearchParams(searchParams);
    if (next === undefined) {
      params.delete("type");
    } else {
      params.set("type", next);
    }
    setSearchParams(params);
  }

  function closeInspector(): void {
    navigate(`${paths.vault}${listSearch}`);
  }

  function onListKey(event: KeyboardEvent<HTMLUListElement>): void {
    if (event.metaKey || event.ctrlKey || event.altKey) {
      return;
    }
    const key = event.key.toLowerCase();
    if (key === "c" || key === "u" || key === "t" || key === "o" || key === "f") {
      const row = document.activeElement instanceof HTMLElement ? document.activeElement.closest<HTMLElement>("[data-entry-id]") : null;
      const entry = row === null ? undefined : all.find((e: Entry): boolean => e.meta.id === row.dataset["entryId"]);
      if (entry === undefined) {
        return;
      }
      event.preventDefault();
      if (key === "c") {
        actions.copyPassword(entry);
      } else if (key === "u") {
        actions.copyUsername(entry);
      } else if (key === "t") {
        actions.copyTotp(entry);
      } else if (key === "o") {
        actions.openUrl(entry);
      } else {
        toggleFavorite.mutate(entry);
      }
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
      return;
    }
    const links = Array.from(event.currentTarget.querySelectorAll<HTMLAnchorElement>("a[data-row]"));
    const index = links.findIndex((link: HTMLAnchorElement): boolean => link === document.activeElement);
    const next = event.key === "ArrowDown" ? Math.min(index + 1, links.length - 1) : Math.max(index - 1, 0);
    const target = links[next];
    if (target !== undefined) {
      event.preventDefault();
      target.focus();
    }
  }

  const loading = entries.isPending && !isTrash;
  const trashItems: VaultMeta[] = trash.data ?? [];

  const list: ReactNode = isTrash ? (
    trash.isPending ? (
      <div className="space-y-3">
        <Skeleton className="h-16" />
        <Skeleton className="h-16" />
      </div>
    ) : trashItems.length === 0 ? (
      <EmptyState icon={Trash2} title="Der Papierkorb ist leer">
        Gelöschte Einträge landen hier, bis du sie wiederherstellst.
      </EmptyState>
    ) : (
      <>
        <div className="mb-4">
          <Alert tone="info">Titel im Papierkorb bleiben verschlüsselt. Nach dem Wiederherstellen erscheinen sie wieder in der Liste.</Alert>
        </div>
        <ul className="divide-y divide-line overflow-hidden rounded-xl border border-line bg-ink-2">
          {trashItems.map((meta: VaultMeta) => (
            <li key={meta.id} className="flex items-center gap-3 px-4 py-3">
              <span className="grid size-9 shrink-0 place-items-center rounded-lg border border-line bg-ink-3 text-mist">
                <Trash2 size={15} aria-hidden="true" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foam">Verschlüsselter Eintrag</p>
                <p className="truncate text-sm text-mist-2">
                  {meta.folder} · gelöscht {meta.deletedAt !== null ? formatRelativeTime(meta.deletedAt, now) : ""}
                </p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                icon={RotateCcw}
                loading={restore.isPending && restore.variables === meta.id}
                onClick={(): void =>
                  restore.mutate(meta.id, {
                    onSuccess: (): void => toast.notify("Eintrag wiederhergestellt.", { tone: "success" }),
                    onError: (): void => toast.notify("Wiederherstellen fehlgeschlagen.", { tone: "danger" })
                  })
                }
              >
                Wiederherstellen
              </Button>
            </li>
          ))}
        </ul>
      </>
    )
  ) : loading ? (
    <div className="space-y-2">
      {[0, 1, 2, 3, 4].map((i: number) => (
        <Skeleton key={i} className="h-14" />
      ))}
    </div>
  ) : entries.isError ? (
    <EmptyState icon={ShieldAlert} title="Der Tresor konnte nicht geladen werden" action={<Button variant="secondary" onClick={(): void => void entries.refetch()}>Erneut versuchen</Button>}>
      Prüfe deine Verbindung. Deine Daten bleiben unverändert.
    </EmptyState>
  ) : all.length === 0 ? (
    <EmptyState
      icon={KeyRound}
      title="Noch ganz leer"
      action={
        <Link to={paths.vaultNew} className={buttonClass("primary")}>
          <Plus size={15} aria-hidden="true" /> Ersten Eintrag anlegen
        </Link>
      }
    >
      Titel und Geheimnisse werden im Browser verschlüsselt. Der Server sieht nur Chiffre.
    </EmptyState>
  ) : visible.length === 0 ? (
    <EmptyState
      icon={Search}
      title="Keine Treffer"
      action={
        query !== "" ? (
          <Button variant="secondary" onClick={(): void => setQuery("")}>
            Suche zurücksetzen
          </Button>
        ) : undefined
      }
    >
      Passe die Suche an oder entferne den aktiven Filter.
    </EmptyState>
  ) : (
    <div className="@container overflow-hidden rounded-xl border border-line bg-ink-2">
      <div aria-hidden="true" className="hidden items-center gap-4 border-b border-line bg-ink/40 py-2 pl-4 pr-[10.375rem] text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-mist-2 @xl:flex">
        <span className="flex-1">Name</span>
        <span className="w-28">Prüfung</span>
        <span className="hidden w-32 @3xl:block">Ordner</span>
        <span className="hidden w-28 text-right @4xl:block">Geändert</span>
      </div>
      <ul
        onKeyDown={onListKey}
        aria-label={`${visible.length} ${visible.length === 1 ? "Eintrag" : "Einträge"}. Pfeiltasten wechseln, C kopiert Passwort, U Benutzernamen, O öffnet Adresse.`}
        className="divide-y divide-line"
      >
        {visible.map((entry: Entry) => {
          const selectedRow = entry.meta.id === itemId;
          const host = displayHost(entry.data.url);
          const entryFlags = flags.get(entry.meta.id) ?? [];
          const labels = fieldLabels(entry.data.type);
          const subtitle = entry.data.type === "note"
            ? "Sichere Notiz"
            : entry.data.username !== "" ? entry.data.username : `Kein Wert für „${labels.username}“`;
          return (
            <li
              key={entry.meta.id}
              data-entry-id={entry.meta.id}
              className={`group relative flex items-center gap-4 pr-2 transition-colors duration-150 ${selectedRow ? "bg-flow/[0.06]" : "hover:bg-foam/[0.03]"}`}
              style={{ contentVisibility: "auto", containIntrinsicSize: "60px" }}
            >
              {selectedRow ? <span className="absolute inset-y-0 left-0 w-0.5 bg-flow" aria-hidden="true" /> : null}
              <Link
                to={`${paths.vaultItem(entry.meta.id)}${listSearch}`}
                data-row
                aria-current={selectedRow ? "true" : undefined}
                className="flex min-w-0 flex-1 items-center gap-4 py-2.5 pl-4 pr-2 outline-none focus-visible:bg-foam/[0.04]"
              >
                <span className="flex min-w-0 flex-1 items-center gap-3">
                  <Avatar label={entry.data.title} />
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-medium text-foam">{entry.data.title}</span>
                      {entry.meta.favorite ? <Star size={12} aria-label="Favorit" className="shrink-0 fill-flow text-flow" /> : null}
                      {hasPasskey(entry.data) ? <Fingerprint size={12} aria-label="Mit Passkey" className="shrink-0 text-mist" /> : null}
                    </span>
                    <span className="block truncate text-[0.8125rem] text-mist">
                      {entry.data.type === "apiKey" || entry.data.type === "license" ? <span className="text-mist-2">{itemTypeLabel(entry.data.type)} · </span> : null}
                      {subtitle}
                      {host !== undefined ? <span className="text-mist-2"> · {host}</span> : null}
                    </span>
                  </span>
                </span>
                <span className="hidden w-28 shrink-0 items-center gap-1 @xl:flex">
                  {entryFlags.length === 0 ? (
                    <span className="text-xs text-mist-2">–</span>
                  ) : (
                    entryFlags.map((flag) => {
                      const Icon = FLAG_ICON[flag];
                      const label = FLAG_LABEL[flag];
                      return (
                        <span key={flag} title={label} className="grid size-6 place-items-center rounded-md bg-buoy/10 text-buoy">
                          <Icon size={12} aria-hidden="true" />
                          <span className="sr-only">{label}</span>
                        </span>
                      );
                    })
                  )}
                </span>
                <span className="hidden w-32 shrink-0 truncate text-[0.8125rem] text-mist @3xl:block">{entry.meta.folder}</span>
                <span className="hidden w-28 shrink-0 text-right text-[0.8125rem] text-mist-2 @4xl:block">
                  {formatRelativeTime(entry.meta.updatedAt, now)}
                </span>
              </Link>
              <div className="flex shrink-0 items-center gap-0.5 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:transition-opacity [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:group-focus-within:opacity-100">
                <IconButton
                  label={entry.meta.favorite ? `${entry.data.title} aus Favoriten entfernen` : `${entry.data.title} zu Favoriten`}
                  icon={Star}
                  size="sm"
                  className={`hidden sm:inline-flex ${entry.meta.favorite ? "text-flow" : ""}`}
                  onClick={(): void => toggleFavorite.mutate(entry)}
                />
                <IconButton
                  label={`Webadresse von ${entry.data.title} öffnen`}
                  icon={ExternalLink}
                  size="sm"
                  disabled={safeHttpUrl(entry.data.url) === undefined}
                  className="hidden sm:inline-flex"
                  onClick={(): void => actions.openUrl(entry)}
                />
                <IconButton
                  label={`${labels.username} für ${entry.data.title} kopieren`}
                  icon={UserRound}
                  size="sm"
                  disabled={entry.data.username === ""}
                  onClick={(): void => actions.copyUsername(entry)}
                />
                <IconButton
                  label={`${labels.password} für ${entry.data.title} kopieren`}
                  icon={Copy}
                  size="sm"
                  disabled={entry.data.password === ""}
                  onClick={(): void => actions.copyPassword(entry)}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );

  const countText = isTrash
    ? `${trashItems.length} ${trashItems.length === 1 ? "Eintrag" : "Einträge"} im Papierkorb`
    : entries.data === undefined
      ? "Wird geladen …"
      : `${visible.length} von ${all.length} ${all.length === 1 ? "Eintrag" : "Einträgen"}`;

  const heading = (
    <PageHeader
      eyebrow="Tresor"
      title={title}
      description={
        <span aria-live="polite">
          {countText}
          {entries.data !== undefined && entries.data.skipped > 0 ? ` · ${entries.data.skipped} nicht lesbar` : ""}
          {!isTrash && breach.status === "error" ? " · Datenleck-Prüfung nicht erreichbar" : ""}
        </span>
      }
      meta={
        filter !== undefined && healthIds !== undefined ? (
          <Link to={`${paths.vault}${folder !== undefined ? `?folder=${encodeURIComponent(folder)}` : ""}`} aria-label={`Filter ${FILTER_TITLES[filter] ?? ""} entfernen`}>
            <Badge tone="buoy" icon={filter === "old" || filter === "reused" || filter === "expiring" || filter === "breached" ? FLAG_ICON[filter] : ShieldAlert}>
              {FILTER_TITLES[filter]}
              <X size={12} aria-hidden="true" />
            </Badge>
          </Link>
        ) : undefined
      }
    />
  );

  const toolbar = !isTrash ? (
    <div className="mt-6 flex flex-col gap-2.5 sm:flex-row sm:items-center">
      <div className="relative min-w-0 flex-1">
        <Search size={15} aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-mist-2" />
        <TextInput
          id="vault-search"
          type="search"
          value={query}
          onChange={(event): void => setQuery(event.target.value)}
          placeholder="Titel, Benutzername oder Adresse suchen"
          aria-label="Einträge durchsuchen"
          className="pl-9 pr-10"
          autoComplete="off"
        />
        <span className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 sm:block" aria-hidden="true">
          <Kbd>/</Kbd>
        </span>
      </div>
      <SelectInput
        aria-label="Typ"
        value={type ?? ""}
        onChange={(event): void => setType(typeFromParam(event.target.value))}
        className="w-full sm:w-48"
      >
        <option value="">Alle Typen</option>
        {TYPE_INFOS.map((info: ItemTypeInfo) => (
          <option key={info.type} value={info.type}>{info.plural}</option>
        ))}
      </SelectInput>
      <SelectInput aria-label="Sortierung" value={order} onChange={(event): void => setOrder(event.target.value === "title" ? "title" : "updated")} className="w-full sm:w-48">
        <option value="updated">Zuletzt geändert</option>
        <option value="title">Name (A–Z)</option>
      </SelectInput>
    </div>
  ) : null;

  return (
    <div className="flex min-h-[calc(100dvh-3.5rem)]">
      <div className="mx-auto w-full min-w-0 max-w-[1120px] flex-1 px-5 py-7 sm:px-8 lg:py-9">
        {heading}
        {toolbar}
        <div className="mt-4">{list}</div>
      </div>

      {wide && inspectorOpen ? (
        <aside
          aria-label={isNew ? "Neuer Eintrag" : "Eintragsdetails"}
          className="sticky top-14 hidden h-[calc(100dvh-3.5rem)] w-[440px] shrink-0 self-start overflow-y-auto border-l border-line bg-ink-2 xl:block"
        >
          <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-line bg-ink-2 px-4">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-mist-2">
              {inspectorMode === "new" ? "Neuer Eintrag" : inspectorMode === "edit" ? "Bearbeiten" : "Details"}
            </p>
            <IconButton label="Details schließen" icon={X} size="sm" onClick={closeInspector} />
          </div>
          <ItemInspector
            mode={inspectorMode}
            entry={selected}
            missing={missing}
            prefill={prefill}
            folders={folders}
            listSearch={listSearch}
          />
        </aside>
      ) : null}

      {!wide ? (
        <Dialog open={inspectorOpen} onClose={closeInspector} title={isNew ? "Neuer Eintrag" : selected?.data.title ?? "Eintrag"} variant="sheet">
          <ItemInspector
            mode={inspectorMode}
            entry={selected}
            missing={missing}
            prefill={prefill}
            folders={folders}
            listSearch={listSearch}
          />
        </Dialog>
      ) : null}
    </div>
  );
}
